import type {
	BulkFinalFieldsFileForm,
	BulkProductDiffsFileForm,
	FinalFieldsListModel,
	FinalFieldsPatchModel,
	HTTPValidationError,
	OptimizationActionResponseModel,
	OptimizationBulkActionsRequestModel,
	OptimizationModel,
	ProductDiffsModel,
} from '@/shared/generated'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { client } from '@/shared/api/client'
import {
	ContentType,
	OptimizationAction,
	OptimizationStatus,
} from '@/shared/generated'

import { bulkFinalFieldsRequestSchema } from '../model/contract'

export function useResetOptimizationMutation(
	options?: UseMutationOptions<OptimizationModel, AxiosError, string>
) {
	return useMutation<OptimizationModel, AxiosError, string>({
		mutationFn: (optimizationGroupId) =>
			client.optimizations.optimizationAction({
				optimizationGroupId,
				action: OptimizationAction.Reset,
			}),
		...(options || {}),
	})
}
export function useUpdateOptimizationWithDiffsMutation(
	optimizationGroupId: string,
	options?: UseMutationOptions<
		OptimizationModel | null,
		AxiosError<HTTPValidationError>,
		ProductDiffsModel
	>
) {
	const queryClient = useQueryClient()

	return useMutation<
		OptimizationModel | null,
		AxiosError<HTTPValidationError>,
		ProductDiffsModel
	>({
		mutationFn: (payload) =>
			client.optimizations.optimizationProductDiffsSku(
				{ optimizationGroupId },
				payload
			),
		...(options || {}),
		onSuccess() {
			queryClient.removeQueries({
				queryKey: ['repricing-statistic', optimizationGroupId],
			})
			queryClient.removeQueries({
				queryKey: ['interpretability-statistic', optimizationGroupId],
			})
		},
	})
}
export function useUpdateOptimizationFinalFieldsMutation(
	optimizationGroupId: string,
	options?: UseMutationOptions<
		OptimizationModel,
		AxiosError<HTTPValidationError>,
		FinalFieldsListModel
	>
) {
	const queryClient = useQueryClient()

	return useMutation<
		OptimizationModel,
		AxiosError<HTTPValidationError>,
		FinalFieldsListModel
	>({
		mutationFn: (payload) =>
			client.optimizations.optimizationFinalFieldsSku(
				{ optimizationGroupId },
				payload
			),
		...(options || {}),
		onSuccess() {
			queryClient.removeQueries({
				queryKey: ['repricing-statistic', optimizationGroupId],
			})
			queryClient.removeQueries({
				queryKey: ['interpretability-statistic', optimizationGroupId],
			})
		},
	})
}
export function useStartOptimizationMutation(
	options?: UseMutationOptions<OptimizationModel, AxiosError, string>
) {
	return useMutation<OptimizationModel, AxiosError, string>({
		mutationFn: (optimizationGroupId) =>
			client.optimizations.optimizationAction({
				optimizationGroupId,
				action: OptimizationAction.Start,
			}),
		...(options || {}),
	})
}
export function useCommitOptimizationMutation(
	options?: UseMutationOptions<OptimizationModel, AxiosError, string>
) {
	return useMutation<OptimizationModel, AxiosError, string>({
		mutationFn: (optimizationGroupId) =>
			client.optimizations.optimizationAction({
				optimizationGroupId,
				action: OptimizationAction.Update,
			}),
		...(options || {}),
	})
}
export function useDeleteExportOptimizationMutation(
	options?: UseMutationOptions<OptimizationModel, AxiosError, string>
) {
	return useMutation<OptimizationModel, AxiosError, string>({
		mutationFn: (optimizationGroupId) =>
			client.optimizations.optimizationAction({
				optimizationGroupId,
				action: OptimizationAction.DeleteExport,
			}),
		...(options || {}),
	})
}
export function useStopOptimizationMutation(
	options?: UseMutationOptions<OptimizationModel, AxiosError, string>
) {
	return useMutation<OptimizationModel, AxiosError, string>({
		mutationFn: (optimizationGroupId) =>
			client.optimizations.optimizationAction({
				optimizationGroupId,
				action: OptimizationAction.Stop,
			}),
		...(options || {}),
	})
}
export function useLockOptimizationMutation(
	options?: UseMutationOptions<OptimizationModel, AxiosError, string>
) {
	return useMutation<OptimizationModel, AxiosError, string>({
		mutationFn: (optimizationGroupId) =>
			client.optimizations.optimizationAction({
				optimizationGroupId,
				action: OptimizationAction.Lock,
			}),
		...(options || {}),
	})
}
export function useUnlockOptimizationMutation(
	options?: UseMutationOptions<OptimizationModel, AxiosError, string>
) {
	return useMutation<OptimizationModel, AxiosError, string>({
		mutationFn: (optimizationGroupId) =>
			client.optimizations.optimizationAction({
				optimizationGroupId,
				action: OptimizationAction.Unlock,
			}),
		...(options || {}),
	})
}
export function useBulkOptimizationMutation(
	options?: UseMutationOptions<
		OptimizationActionResponseModel[],
		AxiosError<HTTPValidationError>,
		OptimizationBulkActionsRequestModel
	>
) {
	return useMutation<
		OptimizationActionResponseModel[],
		AxiosError<HTTPValidationError>,
		OptimizationBulkActionsRequestModel
	>({
		mutationKey: ['bulk-optimizations-action'],
		mutationFn: (payload) =>
			client.optimizations.optimizationBulkActions(payload),
		...(options || {}),
	})
}

const mustBeReset = (status: OptimizationStatus) =>
	![
		OptimizationStatus.New,
		OptimizationStatus.Pending,
		OptimizationStatus.Updating,
	].includes(status)

export const resetOptimizations = async (
	optimizations: OptimizationModel[],
	groupsIds: string[]
) => {
	const payload = optimizations
		.filter((o) =>
			groupsIds?.length ? groupsIds.includes(o.optimization_group_id) : true
		)
		.filter((o) => mustBeReset(o.status))
		.map(({ optimization_group_id }) => ({
			action: OptimizationAction.Reset,
			optimization_group_id,
		}))

	return client.optimizations.optimizationBulkActions(payload)
}
export function useImportOptimizationGroups(
	options?: UseMutationOptions<
		OptimizationActionResponseModel[],
		AxiosError<HTTPValidationError>,
		BulkProductDiffsFileForm
	>
) {
	return useMutation<
		OptimizationActionResponseModel[],
		AxiosError<HTTPValidationError>,
		BulkProductDiffsFileForm
	>({
		mutationFn: (formData) =>
			client.optimizations.optimizationBulkProductDiffsFile(formData, {
				type: ContentType.FormData,
			}),
		...(options || {}),
	})
}
export function useBulkFinalFieldsMutation(
	options?: UseMutationOptions<
		OptimizationActionResponseModel[],
		AxiosError<HTTPValidationError>,
		FinalFieldsPatchModel[]
	>
) {
	return useMutation<
		OptimizationActionResponseModel[],
		AxiosError<HTTPValidationError>,
		FinalFieldsPatchModel[]
	>({
		mutationKey: ['bulk-final-fields'],
		mutationFn: (payload) =>
			client.optimizations.optimizationBulkFinalFields(
				bulkFinalFieldsRequestSchema.parse(payload)
			),
		...(options || {}),
	})
}
export function useBulkFinalFieldsFileMutation(
	options?: UseMutationOptions<
		OptimizationActionResponseModel[],
		AxiosError<HTTPValidationError>,
		BulkFinalFieldsFileForm
	>
) {
	return useMutation<
		OptimizationActionResponseModel[],
		AxiosError<HTTPValidationError>,
		BulkFinalFieldsFileForm
	>({
		mutationKey: ['bulk-final-fields-file'],
		mutationFn: (payload) =>
			client.optimizations.optimizationBulkFinalFieldsFile(payload, {
				type: ContentType.FormData,
			}),
		...(options || {}),
	})
}
